import styled from 'styled-components';
import { CONTROL_GAP, TOGGLE } from '../../../components/uielements/themeToggle/floatingControls';

const SHARE_HEIGHT = 40;

export const ContentContainer = styled.div`
  flex: 1;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.cardBackground};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  box-shadow: ${({ theme }) => `0 0 40px 0 ${theme.colors.shadow}`};
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    min-height: auto;
    height: 100%;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    border-radius: ${({ theme }) => theme.borderRadius.large};
  }
`;

export const ShareButton = styled.button`
  position: absolute;
  top: 20px;
  right: ${({ offset }) => (offset ? '250px' : '100px')};
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primaryForeground};
  padding: 10px 30px;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSizes.normal};
  font-weight: 500;
  white-space: nowrap;
  transition: opacity 0.2s ease;
  z-index: 10;

  &:hover {
    opacity: 0.9;
  }

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    position: fixed;
    top: ${TOGGLE.desktop.top + (TOGGLE.desktop.size - SHARE_HEIGHT) / 2}px;
    right: ${TOGGLE.desktop.right + TOGGLE.desktop.size + CONTROL_GAP}px;
    height: ${SHARE_HEIGHT}px;
    padding: 0 30px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    top: ${TOGGLE.mobile.top + (TOGGLE.mobile.size - SHARE_HEIGHT) / 2}px;
    right: ${TOGGLE.mobile.right + TOGGLE.mobile.size + CONTROL_GAP}px;
    width: ${SHARE_HEIGHT}px;
    padding: 0;
    justify-content: center;
    border-radius: 50%;

    span {
      display: none;
    }

    svg {
      width: 18px;
      height: 18px;
    }
  }
`;
export const StartDAButton = styled(ShareButton)`
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

export const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 50px;
  margin-bottom: 30px;

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    padding-top: 120px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding-top: 80px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding-top: 60px;
  }
`;

export const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  margin-bottom: 15px;
`;

export const LogoCircle = styled.div`
  width: 85px;
  height: 84px;
  background-color: ${({ theme }) => theme.colors.cardBackground};
  border-radius: 41px;
  box-shadow: ${({ theme }) => `0 0 40px 0 ${theme.colors.blue}`};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  img {
    width: 82px;
    height: 82px;
    object-fit: cover;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 70px;
    height: 70px;

    img {
      width: 68px;
      height: 68px;
    }
  }
`;

export const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontSizes.medium};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  letter-spacing: -0.25px;
  margin: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.fontSizes.normal};
  }
`;

export const Description = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.normal};
  text-align: justify;
  color: ${({ theme }) => theme.colors.text};
  line-height: 25px;
  width: min(932px, calc(100% - 80px));
  margin: 0 auto;

  .bold {
    font-weight: 700;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    width: calc(100% - 80px);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.fontSizes.small};
    line-height: 22px;
    width: calc(100% - 48px);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: calc(100% - 40px);
  }
`;

export const MessagesScrollContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  margin-bottom: 100px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-bottom: 100px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-bottom: 80px;
  }

  /* Custom scrollbar */
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => `${theme.colors.border} transparent`};

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.border};
    border-radius: 3px;
  }
`;

export const MessageWrapper = styled.div`
  display: flex;
  margin: 0 auto 1rem;
  width: min(932px, calc(100% - 80px));
  justify-content: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};

  &:first-child {
    margin-top: 1rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    width: calc(100% - 80px);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: calc(100% - 48px);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: calc(100% - 40px);
  }
`;

export const MessageBubble = styled.div`
  max-width: 70%;
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
  padding: 0.75rem 1rem;
  font-size: ${({ theme }) => theme.fontSizes.normal};
  border-radius: 12px;
  background-color: ${({ $isUser, theme }) =>
    $isUser ? theme.colors.primary : theme.colors.inputBackground};
  color: ${({ $isUser, theme }) => ($isUser ? theme.colors.primaryForeground : theme.colors.text)};
  box-shadow: ${({ theme }) => `0 0 10px ${theme.colors.shadow}`};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    max-width: 85%;
    padding: 0.625rem 0.875rem;
    font-size: ${({ theme }) => theme.fontSizes.small};
  }
`;

export const InputSection = styled.div`
  position: absolute;
  bottom: 14px;
  left: 50%;
  transform: translateX(-50%);
  width: min(932px, calc(100% - 80px));

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    width: calc(100% - 80px);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: calc(100% - 48px);
    bottom: 30px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: calc(100% - 40px);
    bottom: 20px;
  }
`;

export const InputContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.inputBackground};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => `0 0px 20px 0 ${theme.colors.shadowInput}`};
  position: relative;
  display: flex;
  align-items: center;
  padding: 0 0 0 ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 0 0 0 ${({ theme }) => theme.spacing.md};
  }
`;

export const Input = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-size: ${({ theme }) => theme.fontSizes.normal};
  color: ${({ theme }) => theme.colors.text};
  font-family: ${({ theme }) => theme.fonts.primary};

  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.fontSizes.small};
  }
`;

export const SendButton = styled.button`
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text};
  transition: opacity 0.2s ease;
  flex-shrink: 0;

  &:hover {
    opacity: 0.7;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 24px;
    height: 24px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 40px;
    height: 40px;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

export const MobileMenuButton = styled.button`
  display: none;

  /* Tablet + Mobile */
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: flex;
    position: absolute;
    top: 20px;
    left: 20px;
    z-index: 100;
    width: 44px;
    height: 44px;
    align-items: center;
    justify-content: center;
    background-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primaryForeground};
    border: none;
    border-radius: ${({ theme }) => theme.borderRadius.small};
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    svg {
      width: 24px;
      height: 24px;
    }
  }
`;

export const VoiceButton = styled.button`
  position: absolute;
  right: 35px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $isListening, theme }) => ($isListening ? '#dc3545' : theme.colors.textSecondary)};
  transition: all 0.2s ease;
  border-radius: ${({ theme }) => theme.borderRadius.small};

  &:hover {
    background-color: ${({ theme }) => theme.colors.border};
  }

  svg {
    width: 20px;
    height: 20px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    right: 40px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    right: 30px;

    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

export const ListeningIndicator = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  padding: 0.5rem 1rem;
  background-color: #dc3545;
  color: #fff;
  border-radius: 8px;
  margin-bottom: 0.5rem;
  font-size: ${({ theme }) => theme.fontSizes.small};
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 0.375rem 0.75rem;
  }
`;

export const AddressSuggestionsWrapper = styled.div`
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  z-index: 10;
`;

export const HelperText = styled.div`
  text-align: center;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 8px;
  padding: 0 16px;

  @media (max-width: 768px) {
    font-size: 11px;
    margin-top: 6px;
  }
`;

export const TextArea = styled.textarea`
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  padding: 8px;
  resize: none;
  overflow-y: auto;
  font-family: inherit;

  font-size: ${({ theme }) => theme.fontSizes.normal};
  color: ${({ theme }) => theme.colors.text};
  font-family: ${({ theme }) => theme.fonts.primary};

  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.fontSizes.small};
  }

  /* Auto-grow but limit to max 4 lines (approximately 96px = 24px * 4) */
  min-height: 24px;
  max-height: 96px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  /* Hide scrollbar for cleaner look */
  scrollbar-width: thin;
  scrollbar-color: #ccc transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 2px;
  }
`;

export const LimitWarning = styled.div`
  text-align: center;
  font-size: 12px;

  color: #e5484d;
  margin: 0 auto ${({ theme }) => theme.spacing.xs};
  white-space: nowrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 11px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    white-space: normal;
  }
`;
